import { useEffect, useRef, useState } from "react";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { featureFlags } from "@/config/featureFlags";

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) ?? "";

// After 10 s in "loading" with no callback, surface an error so the user
// isn't permanently stuck on a silent stall (Safari ITP, content filter, etc.)
const LOADING_TIMEOUT_MS = 10_000;

export type TurnstileStatus = "loading" | "interactive" | "ready" | "error";

export interface UseTurnstileReturn {
  ref: React.MutableRefObject<TurnstileInstance | undefined>;
  status: TurnstileStatus;
  token: string | undefined;
  isReady: boolean;
  showError: boolean;
  /**
   * FE kill-switch mirror of the BE TURNSTILE_ENABLED flag. When `false`,
   * consumers must not render the widget; the hook reports ready-with-no-token
   * so submit gates open and requests go out without a `turnstileToken`
   * (which the BE ignores when its flag is off).
   */
  enabled: boolean;
  siteKey: string;
  onSuccess: (token: string) => void;
  onError: () => void;
  onExpire: () => void;
  onBeforeInteractive: () => void;
  onAfterInteractive: () => void;
  reset: () => void;
}

export function useTurnstile(): UseTurnstileReturn {
  const enabled = featureFlags.turnstile;
  const ref = useRef<TurnstileInstance | undefined>(undefined);
  // Disabled: no widget will ever fire a callback, so start (and stay) ready.
  const [status, setStatus] = useState<TurnstileStatus>(
    enabled ? "loading" : "ready",
  );
  const [token, setToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (status !== "loading") {
      return;
    }
    const timer = setTimeout(() => setStatus("error"), LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const onSuccess = (t: string) => {
    setToken(t);
    setStatus("ready");
  };

  const onError = () => {
    setToken(undefined);
    setStatus("error");
  };

  const onExpire = () => {
    setToken(undefined);
    setStatus("loading");
  };

  // Cloudflare is showing the checkbox — widget is visible, waiting for user
  const onBeforeInteractive = () => setStatus("interactive");

  // User clicked the checkbox — Cloudflare is processing the response
  const onAfterInteractive = () => setStatus("loading");

  const reset = () => {
    // No widget mounted — going back to "loading" would disable submit forever.
    if (!enabled) {
      return;
    }
    setToken(undefined);
    setStatus("loading");
    ref.current?.reset();
  };

  return {
    ref,
    status,
    token,
    isReady: status === "ready",
    showError: status === "error",
    enabled,
    siteKey: SITE_KEY,
    onSuccess,
    onError,
    onExpire,
    onBeforeInteractive,
    onAfterInteractive,
    reset,
  };
}
